import { useState } from 'react'

import { clsx } from 'clsx'
import { Link } from 'react-router-dom'

import { ClientError } from '@tribeplatform/gql-client/lib'
import {
  BadgeType,
  MassActionRequest,
  MassActionRequestContext,
  Member,
  MemberListFilterByInput,
  Space,
  Tag,
} from '@tribeplatform/gql-client/types'
import {
  MemberPicker,
  SpacePicker,
  TagPicker,
} from '@tribeplatform/react-components/EntityPicker'
import { T, useI18n, useStaticIntl } from '@tribeplatform/react-components/i18n'
import { MemberBadge } from '@tribeplatform/react-components/MemberBadge'
import {
  useCreateMassActionRequest,
  useNetwork,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Checkbox } from '@tribeplatform/react-ui-kit/Checkbox'
import {
  DateTimePicker,
  DateValue,
} from '@tribeplatform/react-ui-kit/DateTimePicker'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { Container } from '@tribeplatform/react-ui-kit/Layout'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { getNormalizedAction, MassActionSettingsFactory } from './constants.js'
import { MassActionRequestAction } from './types.js'

export interface MassActionModalProps {
  open: boolean
  onClose: () => void
  context: MassActionRequestContext
  action: MassActionRequestAction
  filterBy: MemberListFilterByInput[]
  query?: string
  count: number
  onSuccess: (m: MassActionRequest) => void
}

export const MassActionModal = ({
  open,
  onClose,
  context,
  action,
  filterBy,
  query,
  count,
  onSuccess,
}: MassActionModalProps) => {
  const { $t } = useI18n()
  const settings = useStaticIntl(
    MassActionSettingsFactory,
    context,
    action,
    count,
  )
  const { mutateAsync: createMassActionRequest } = useCreateMassActionRequest()
  const {
    data: { badges },
  } = useNetwork()

  const [space, setSpace] = useState<Space>(null)
  const [badgeIds, setBadgeIds] = useState<string[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [removeAllTags, setRemoveAllTags] = useState<boolean>()
  const [owner, setOwner] = useState<Member>(null)
  const [publishedAt, setPublishedAt] = useState<DateValue>(null)

  const assignableBadges = badges
    ?.filter(badge => badge.active && badge.type === BadgeType.Manual)
    ?.map(badge => ({
      ...badge,
      isAssigned: badgeIds.includes(badge.id),
      onClick: () => {
        if (badgeIds.includes(badge.id)) {
          setBadgeIds(badgeIds.filter(id => id !== badge.id))
        } else {
          setBadgeIds([...badgeIds, badge.id])
        }
      },
    }))

  const { title } = settings

  const getButtonDisabled = () => {
    if (
      action === MassActionRequestAction.AssignBadge ||
      action === MassActionRequestAction.RevokeBadge
    ) {
      return !badgeIds.length
    }
    if (action === MassActionRequestAction.AddToSpace) {
      return !space
    }

    if (action === MassActionRequestAction.AddTag) {
      return !tags.length
    }

    if (action === MassActionRequestAction.RemoveTag) {
      return !tags.length && !removeAllTags
    }

    return false
  }

  const performMassAction = async () => {
    try {
      const massActionRequest = await createMassActionRequest({
        input: {
          context,
          action: getNormalizedAction(action),
          filters: {
            query,
            filterBy,
          },
          extraProperties: {
            spaceId: space?.id,
            badgeIds,
            tagIds: tags.map(tag => tag.id),
            removeAllTags,
            updatePostProperties: {
              ownerId: owner?.id,
              publishedAt: (publishedAt as Date)?.toISOString(),
            },
          },
        },
      })
      toast({
        title: $t({
          defaultMessage: 'Action has been initialized',
          id: 'MassAction.SuccessNotice',
        }),
        description:
          settings?.toastMessage ??
          $t({
            defaultMessage: 'This action might take a few minutes to complete.',
            id: 'MassAction.SuccessNotice.Description',
          }),
        status: 'info',
      })
      onSuccess(massActionRequest)
      onClose()
    } catch (e) {
      const errors = (e as ClientError)?.response?.errors
      toast({
        title:
          errors?.[0]?.message ??
          $t({
            defaultMessage: 'Unable to perform mass action',
            id: 'MassAction.Failed',
          }),
        status: 'error',
      })
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header title={title} description={settings?.description} />
      <Modal.Content>
        <Container size="full" direction="vertical" spacing="sm">
          {action === MassActionRequestAction.Move && (
            <>
              <FormControl.Label>
                <T defaultMessage="Move to" id="Generics.MoveTo" />
              </FormControl.Label>
              <SpacePicker value={space} onChange={setSpace} />
            </>
          )}
          {action === MassActionRequestAction.AddTag && (
            <>
              <FormControl.Label>
                <T defaultMessage="Select tags" id="Generics.SelectTags" />
              </FormControl.Label>
              <TagPicker creatable multiple value={tags} onChange={setTags} />
            </>
          )}
          {action === MassActionRequestAction.RemoveTag && (
            <>
              <FormControl.Label>
                <T defaultMessage="Select tags" id="Generics.SelectTags" />
              </FormControl.Label>
              <TagPicker
                disabled={removeAllTags}
                creatable
                multiple
                value={tags}
                onChange={setTags}
              />
              <div className="py-0.5">
                <Checkbox
                  checked={removeAllTags}
                  onClick={() => setRemoveAllTags(c => !c)}
                >
                  <p className="font-medium -ms-1 -mt-0.5 text-content text-base">
                    <T
                      defaultMessage="Remove all {TAGS}"
                      id="Generics.RemoveAllTags"
                    />
                  </p>
                </Checkbox>

                <p className="text-content-subdued text-sm">
                  <T
                    defaultMessage="Remove all {TAGS} from selected posts"
                    id="MassAction.Post.RemoveTag.RemoveAllDesc"
                  />
                </p>
              </div>
            </>
          )}
          {action === MassActionRequestAction.UpdateOwner && (
            <>
              <FormControl.Label>
                <T defaultMessage="Author" id="Generics.Author" />
              </FormControl.Label>
              <MemberPicker value={owner} onChange={setOwner} />
            </>
          )}
          {action === MassActionRequestAction.UpdatePublishedAt && (
            <>
              <FormControl.Label>
                <T defaultMessage="Publish time" id="Generics.PublishTime" />
              </FormControl.Label>
              <DateTimePicker
                value={publishedAt}
                onChange={setPublishedAt}
                placeholder={new Date().toLocaleDateString()}
              />
            </>
          )}
          {action === MassActionRequestAction.AddToSpace && (
            <>
              <FormControl.Label>
                <T defaultMessage="Add to" id="Generics.AddTo" />
              </FormControl.Label>
              <SpacePicker value={space} onChange={setSpace} />
            </>
          )}
          {(action === MassActionRequestAction.AssignBadge ||
            action === MassActionRequestAction.RevokeBadge) && (
            <>
              {assignableBadges?.map(badge => (
                <div key={badge.id} className="group">
                  <Link
                    onClick={badge.onClick}
                    to="#"
                    className={clsx(
                      'flex-1 border border-line-subdued rounded-base p-2 px-3 mb-2 bg-surface hover:bg-surface-hovered flex space-s-2 items-center',
                    )}
                  >
                    <div className="pointer-events-none cursor-pointer">
                      <Checkbox checked={badge.isAssigned} readOnly />
                    </div>
                    <span className="flex-grow">{badge.name}</span>
                    <MemberBadge badge={badge} />
                  </Link>
                </div>
              ))}
              {!assignableBadges?.length && (
                <div className="pb-3">
                  <p>
                    <T
                      defaultMessage="No active badge is available to assign."
                      id="Badges.NoActives"
                    />
                  </p>
                  <p>
                    <T
                      defaultMessage="Please create or activate a badge in your community."
                      id="Badges.NoActives.CTA"
                    />
                  </p>
                </div>
              )}
            </>
          )}
          <p className="text-content-muted font-medium">
            {settings?.countConfirmationMsg}
          </p>
        </Container>
      </Modal.Content>
      <Modal.Actions>
        <Button
          variant={settings?.buttonVariant}
          destructive={settings?.buttonDestructive}
          onClick={performMassAction}
          disabled={getButtonDisabled()}
        >
          {settings?.buttonText}
        </Button>
        <Button variant="secondaryNeutral" onClick={onClose}>
          <T defaultMessage="Cancel" id="Generics.Cancel" />
        </Button>
      </Modal.Actions>
    </Modal>
  )
}
